/**
 * adminFetch — gemeinsamer HTTP-Helfer fuer die Admin-Hooks.
 *
 * Die Admin-Endpunkte stehen (noch) nicht in api-types/openapi.yaml, deshalb
 * laeuft der Zugriff bewusst ueber rohes fetch statt ueber den typisierten
 * apiClient — genau wie in useAdminUsers/useAdminNewsletters. Bis hierher war
 * das Trio baseUrl/Bearer-Header/JSON-Fehler in jedem Admin-Hook einzeln
 * kopiert; mit useAdminRevenue waere es die vierte Kopie gewesen. Daher einmal
 * zentral, aber bewusst ohne Interceptor oder Token-Refresh: das ist Sache des
 * apiClient und wuerde hier nur Komplexitaet ohne Nutzen erzeugen.
 */

import { useAuthStore } from '../../stores/authStore'

export function apiUrl(path: string): string {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
  return baseUrl + path
}

export function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken ?? null
  return token ? { Authorization: 'Bearer ' + token } : {}
}

/**
 * Fuehrt einen Admin-Request aus und wirft bei !ok einen Error.
 *
 * `fallbackMessage` ist die Meldung, die angezeigt wird, wenn der Server keinen
 * eigenen `error`-Text mitschickt — die Admin-API antwortet im Fehlerfall mit
 * `{ error: '...' }` (siehe Api::V2::Admin::BaseController).
 */
export async function adminFetch<T>(
  path: string,
  fallbackMessage: string,
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(apiUrl(path), {
    ...init,
    headers: { ...authHeaders(), ...(init?.headers ?? {}) },
  })
  if (!response.ok) {
    const json = await response.json().catch(() => ({}) as { error?: string })
    throw new Error(json.error ?? fallbackMessage)
  }
  return response.json() as Promise<T>
}
